import React, { useState, useEffect, useRef } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import JustGage from "justgage";
import axios from "axios";
import "./Oee.css";

ChartJS.register(ArcElement, Tooltip, Legend);

const OEECalculator = () => {
  const [formData, setFormData] = useState({
    totalcount: "",
    scrapcount: "",
    otherbreaks: "",
  });
  const [results, setResults] = useState(null);
  const [chartData, setChartData] = useState(null);

  const availabilityGaugeRef = useRef(null);
  const performanceGaugeRef = useRef(null);
  const qualityGaugeRef = useRef(null);
  const oeeGaugeRef = useRef(null);

  useEffect(() => {
    if (results) {
      updateGauges();
      updateChartData();
    }
  }, [results]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { totalcount, scrapcount, otherbreaks } = formData;

    const totalProductionTime = 480;
    const actualOperatingTime = 420 - parseFloat(otherbreaks);
    const availability = (actualOperatingTime / totalProductionTime) * 100;
    const goodcount = parseFloat(totalcount) - parseFloat(scrapcount);

    try {
      const response = await axios.get(
        "https://cmti-edge.online/smddc/machinedate.php"
      );
      const machineTimeHours = response.data.machine_time_hours;
      const performance = calculatePerformance(
        machineTimeHours,
        actualOperatingTime
      );
      const quality = (goodcount / parseFloat(totalcount)) * 100;
      const oee = (availability * performance * quality) / 10000;

      setResults({
        oee,
        availability,
        performance,
        quality,
      });

      sendDataToBackend(
        totalcount,
        scrapcount,
        availability,
        performance,
        quality,
        oee
      );
    } catch (error) {
      console.error("Error fetching machining time data:", error);
    }
  };

  const calculatePerformance = (machineTimeHours, actualOperatingTime) => {
    const machineTimeMinutes = machineTimeHours * 60;
    return (machineTimeMinutes / actualOperatingTime) * 100;
  };

  const sendDataToBackend = async (
    totalcount,
    scrapcount,
    availability,
    performance,
    quality,
    oee
  ) => {
    try {
      await axios.post("https://cmti-edge.online/smddc/c.php", {
        totalcount,
        scrapcount,
        availability,
        performance,
        quality,
        oee,
      });
      console.log("Data sent successfully");
    } catch (error) {
      console.error("Error sending data:", error);
    }
  };

  const updateGauges = () => {
    updateGauge(availabilityGaugeRef.current, results.availability, "#4CAF50");
    updateGauge(performanceGaugeRef.current, results.performance, "#2196F3");
    updateGauge(qualityGaugeRef.current, results.quality, "#FF5722");

    if (oeeGaugeRef.current && oeeGaugeRef.current.refresh) {
      oeeGaugeRef.current.refresh(results.oee);
    } else {
      oeeGaugeRef.current = new JustGage({
        id: "oeeGauge",
        value: results.oee,
        min: 0,
        max: 100,
        width: 350,
        height: 350,
        gaugeWidthScale: 0.8,
        counter: true,
        hideInnerShadow: true,
        customSectors: [
          { color: "#FF0000", lo: 0, hi: 60 },
          { color: "#FFA500", lo: 60, hi: 85 },
          { color: "#00FF00", lo: 85, hi: 100 },
        ],
        levelColorsGradient: false,
        valueFontColor: "#FFFFFF",
        hideMinMax: false,
        title: "OEE",
        label: "%",
        levelColors: ["purple"],
      });
    }
  };

  const updateGauge = (gaugeElement, percentage, color) => {
    if (!gaugeElement) return;

    const circle = gaugeElement.querySelector(".foreground-circle");
    const resultValue = gaugeElement.querySelector(".result-value");

    const circumference = 2 * Math.PI * 65;
    const dashOffset = circumference * (1 - percentage / 100);

    circle.style.strokeDasharray = `${circumference} ${circumference}`;
    circle.style.strokeDashoffset = dashOffset;
    circle.style.stroke = color;

    resultValue.textContent = `${percentage.toFixed(2)}%`;
  };

  const updateChartData = () => {
    const { oee } = results;
    const downtimePercentage =
      ((60 + parseFloat(formData.otherbreaks)) / 420) * 100;
    const goodUnitsPercentage =
      ((parseFloat(formData.totalcount) - parseFloat(formData.scrapcount)) /
        parseFloat(formData.totalcount)) *
      100;
    const badUnitsPercentage = 100 - goodUnitsPercentage;
    const oeeLoss = 100 - oee;

    setChartData({
      labels: ["OEE", "Downtime", "Good Units", "Bad Units", "OEE Loss"],
      datasets: [
        {
          data: [
            oee,
            downtimePercentage,
            goodUnitsPercentage,
            badUnitsPercentage,
            oeeLoss,
          ],
          backgroundColor: ["red", "blue", "orange", "#cc0066", "#33cc33"],
          borderWidth: 1,
        },
      ],
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 bg-gray-800 pr-2 text-white">
      <div className="form-container p-4 bg-gray-900 rounded-lg shadow-lg">
        <h3 className="text-center text-yellow-300 mb-4">OEE Calculator</h3>
        <form id="oeeForm" onSubmit={handleSubmit}>
          <div className="form-group mb-4 mt-5">
            <label htmlFor="totalcount" className="block mb-2">
              Total Parts Produced
            </label>
            <input
              type="number"
              id="totalcount"
              className="form-control w-full p-2 rounded bg-gray-700 text-white"
              required
              value={formData.totalcount}
              onChange={handleInputChange}
            />

            <label htmlFor="scrapcount" className="block mb-2 mt-4">
              Scrap Parts Produced
            </label>
            <input
              type="number"
              id="scrapcount"
              className="form-control w-full p-2 rounded bg-gray-700 text-white"
              required
              value={formData.scrapcount}
              onChange={handleInputChange}
            />

            <label htmlFor="otherbreaks" className="block mb-2 mt-4">
              Other Break time (minutes)
            </label>
            <input
              type="number"
              id="otherbreaks"
              className="form-control w-full p-2 rounded bg-gray-700 text-white"
              required
              value={formData.otherbreaks}
              onChange={handleInputChange}
            />
          </div>
          <input
            type="submit"
            value="Calculate"
            className="w-full p-2 bg-blue-500 hover:bg-blue-700 rounded mt-4 cursor-pointer transition duration-300"
          />
        </form>
        {results && (
          <div id="result" className="mt-4">
            <div className="result-item mb-2">
              <span className="result-label block">OEE:</span>
              <span className="result-value text-green-500">
                {results.oee.toFixed(2)}%
              </span>
            </div>
            <div className="result-item mb-2">
              <span className="result-label block">Availability:</span>
              <span className="result-value">
                {results.availability.toFixed(2)}%
              </span>
            </div>
            <div className="result-item mb-2">
              <span className="result-label block">Performance:</span>
              <span className="result-value">
                {results.performance.toFixed(2)}%
              </span>
            </div>
            <div className="result-item mb-2">
              <span className="result-label block">Quality:</span>
              <span className="result-value">
                {results.quality.toFixed(2)}%
              </span>
            </div>
          </div>
        )}
      </div>

      <div className=" flex flex-wrap justify-around p-4 bg-gray-900 rounded-lg shadow-lg col-span-full lg:col-span-2">
        <div
          id="availabilityGauge"
          ref={availabilityGaugeRef}
          className="mb-4 mt-10"
        >
          <h3 className="mb-4 text-2xl">Availability</h3>
          <svg className="circle  mx-auto">
            <circle
              className="background-circle"
              r="95"
              cx="105"
              cy="105"
              stroke="#ddd"
            />
            <circle
              className="foreground-circle"
              r="95"
              cx="105"
              cy="105"
              stroke="#4CAF50"
            />
            <text
              x="50%"
              y="50%"
              alignmentBaseline="middle"
              textAnchor="middle"
              fontSize="20"
              fill="#fff"
            >
              <tspan
                className="result-value"
                dy="0"
                style={{ fontSize: "20px", fontWeight: "bold", color: "white" }}
              ></tspan>
            </text>
          </svg>
        </div>
        <div
          id="performanceGauge"
          ref={performanceGaugeRef}
          className="mb-4 mt-10"
        >
          <h3 className="mb-4 text-2xl">Performance</h3>
          <svg className="circle w-32 h-32 mx-auto">
            <circle
              className="background-circle"
              r="95"
              cx="105"
              cy="105"
              stroke="#ddd"
            />
            <circle
              className="foreground-circle"
              r="95"
              cx="105"
              cy="105"
              stroke="#2196F3"
            />
            <text
              x="50%"
              y="50%"
              alignmentBaseline="middle"
              textAnchor="middle"
              fontSize="20"
              fill="#fff"
            >
              <tspan
                className="result-value"
                dy="0"
                style={{ fontSize: "20px", fontWeight: "bold", color: "white" }}
              ></tspan>
            </text>
          </svg>
        </div>
        <div id="qualityGauge" className="mt-10" ref={qualityGaugeRef}>
          <h3 className="mb-4 text-2xl">Quality</h3>
          <svg className="circle w-32 h-32 mx-auto">
            <circle
              className="background-circle"
              r="95"
              cx="105"
              cy="105"
              stroke="#ddd"
            />
            <circle
              className="foreground-circle"
              r="95"
              cx="105"
              cy="105"
              stroke="#FF5722"
            />
            <text
              x="50%"
              y="50%"
              alignmentBaseline="middle"
              textAnchor="middle"
              fontSize="20"
              fill="#fff"
            >
              <tspan
                className="result-value"
                dy="0"
                style={{ fontSize: "20px", fontWeight: "bold", color: "white" }}
              ></tspan>
            </text>
          </svg>
        </div>

        <div className="oee-container text-center p-4 bg-gray-900 rounded-lg shadow-lg col-span-full lg:col-span-1">
          <h3 className=" text-2xl mt-10">OEE %</h3>
          <div id="oeeGauge" className="text-center -mt-12 w-96 h-80">
            <div
              className="result-value text-4xl text-green-500"
              ref={oeeGaugeRef}
            ></div>
          </div>
        </div>
      </div>

      <div className="ch-container text-center p-4 bg-gray-900 rounded-lg shadow-lg col-span-full">
        <h3 className="mb-4">OEE LOSSES(%)</h3>
        {chartData && (
          <Pie
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  labels: {
                    color: "white",
                  },
                },
                title: {
                  display: true,
                  text: "OEE and Production Breakdown",
                  color: "white",
                },
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default OEECalculator;
